import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, type ApiError } from "../api";
import { DiscordMark, Icon } from "../icons";
import { Avatar, Badge, EmptyState, Field, Skeleton, formatDate, formatNumber } from "../ui";
import { CopyButton, ErrorState, Notice, relative, useResource } from "../ui/kit";
import { AreaGate, SiteShell, loginPath } from "./SiteShell";
import { useSite } from "./context";

/* ---------- Join ---------- */

type Join = { connect: { java: string | null; bedrock: { host: string; port: number } | null }; server: { online: boolean; players: number; max: number; version: string | null }; rules: string; invite: string | null; rulesLink: string | null };

export function JoinPage() {
  return <AreaGate id="public.join"><SiteShell><JoinBody/></SiteShell></AreaGate>;
}

function JoinBody() {
  const state = useResource<Join>("/api/v1/public/join");
  const data = state.data;
  return <main id="main">
    <div className="container page-top"><header className="page-head">
      <h1>Join SkyBoxy</h1>
      <p>Three steps and you are on your island. Java and Bedrock players share the same server.</p>
      {data && <p className="join-status"><span className={`live-pulse ${data.server.online ? "" : "paused"}`} aria-hidden="true"/>{data.server.online ? <span><strong>{formatNumber(data.server.players)} playing now</strong>{data.server.version ? ` · Minecraft ${data.server.version}` : ""}</span> : <span>The server is offline right now. You can still get set up.</span>}</p>}
    </header></div>
    <section className="band" aria-label="How to join">
      <div className="container">
        {state.loading && !data ? <Skeleton rows={4} label="Loading join details"/> : state.error || !data ? <ErrorState error={state.error}/> : <ol className="join-flow">
          <JoinStep number={1} title="Add the server" description="Open Minecraft. On Java, go to Multiplayer and choose Add Server. On Bedrock, open the Servers tab and choose Add Server.">
            {data.connect.java || data.connect.bedrock ? <div className="join-addresses">
              {data.connect.java && <div className="address-card join-address"><div><small>Java Edition</small><strong>{data.connect.java}</strong></div><CopyButton value={data.connect.java} label="Copy" ariaLabel="Copy the Java address" className="button primary"/></div>}
              {data.connect.bedrock && <div className="address-card join-address"><div><small>Bedrock Edition · port {data.connect.bedrock.port}</small><strong>{data.connect.bedrock.host}</strong></div><CopyButton value={data.connect.bedrock.host} label="Copy" ariaLabel="Copy the Bedrock address" className="button"/></div>}
            </div> : <EmptyState icon="server" title="Address coming soon" description="The server address will be posted here soon. Ask in the Discord in the meantime." compact/>}
          </JoinStep>
          <JoinStep number={2} title="Join the Discord" description="Events, giveaways, support tickets, and the people you will play with are all there. Signing in with Discord also opens the member portal.">
            {data.invite ? <div><a className="button discord" href={data.invite} target="_blank" rel="noopener noreferrer"><DiscordMark size={16}/>Join the Discord<span className="sr-only"> (opens in a new tab)</span></a></div> : <p className="join-muted">Ask a player in game for an invite.</p>}
          </JoinStep>
          <JoinStep number={3} title="Read the rules" description="Here is the short version. Breaking them can get you muted or banned, so take a minute.">
            <div className="join-rules">{data.rules.trim() ? <RulesText text={data.rules}/> : <p>Be kind, play fair, and listen to staff.</p>}</div>
            {data.rulesLink && <a className="section-link" href={data.rulesLink} target="_blank" rel="noopener noreferrer">Read the full rules<Icon name="external" size={16}/><span className="sr-only"> (opens in a new tab)</span></a>}
          </JoinStep>
        </ol>}
      </div>
    </section>
  </main>;
}

function JoinStep({ number, title, description, children }: { number: number; title: string; description: string; children: ReactNode }) {
  return <li className="join-row">
    <div className="join-copy"><span className="join-number" aria-hidden="true">{number}</span><h2>{title}</h2><p>{description}</p></div>
    <div className="join-body">{children}</div>
  </li>;
}

// A tiny, safe subset of Markdown: headings, bullet lists, and paragraphs. Everything renders as text.
function RulesText({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => { if (list.length) { blocks.push(<ul key={`l${blocks.length}`}>{list.map((item, index) => <li key={index}>{item}</li>)}</ul>); list = []; } };
  for (const line of text.split(/\r?\n/)) {
    if (/^\s*[-*]\s+/.test(line)) { list.push(line.replace(/^\s*[-*]\s+/, "")); continue; }
    flush();
    if (/^#{1,3}\s+/.test(line)) blocks.push(<h3 key={`h${blocks.length}`}>{line.replace(/^#{1,3}\s+/, "")}</h3>);
    else if (line.trim()) blocks.push(<p key={`p${blocks.length}`}>{line}</p>);
  }
  flush();
  return <>{blocks}</>;
}

/* ---------- Team ---------- */

type TeamMember = { id: string; displayName: string; avatarUrl: string | null; tier: string; title: string | null; role: { name: string; color: string } | null };
const tierOrder = ["Owner", "Admin", "Moderator", "Analyst", "Add-on manager", "Staff"];
const tierHeading = (tier: string, count: number) => tier === "Staff" ? "Staff" : `${tier}${count === 1 ? "" : "s"}`;

export function TeamPage() {
  return <AreaGate id="public.team"><SiteShell><TeamBody/></SiteShell></AreaGate>;
}

function TeamBody() {
  const state = useResource<{ items: TeamMember[] }>("/api/v1/public/team");
  const items = state.data?.items || [];
  const known = new Set(tierOrder);
  const groups = [...tierOrder, ...new Set(items.map((item) => item.tier).filter((tier) => !known.has(tier)))]
    .map((tier) => ({ tier, people: items.filter((item) => item.tier === tier) })).filter((group) => group.people.length);
  return <main id="main">
    <div className="container page-top"><header className="page-head"><h1>The team</h1><p>The people who run SkyBoxy. If you need help, open a ticket in the Discord or from your member portal and one of us will answer.</p></header></div>
    <section className="band" aria-label="Staff">
      <div className="container">
        {state.loading && !state.data ? <Skeleton rows={3} label="Loading the team"/> : state.error ? <ErrorState error={state.error}/>
          : !items.length ? <EmptyState icon="members" title="The team page is not set up yet" description="The staff list has not been published. If you need help now, ask in the Discord and someone will get back to you." action={<div className="empty-actions"><Link className="button" to="/join">How to join</Link></div>}/>
          : <div className="team-roster">{groups.map((group) => { const id = `tier-${group.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`; return <section key={group.tier} className="team-tier" aria-labelledby={id}>
            <h2 id={id}>{tierHeading(group.tier, group.people.length)}<span className="team-count">{group.people.length}</span></h2>
            <ul className="team-people">{group.people.map((person) => <li key={person.id}>
              <Avatar src={person.avatarUrl} name={person.displayName} size={56}/>
              <span className="team-person">
                <strong>{person.displayName}</strong>
                <small>{person.role?.color && !person.title ? <span className="role-swatch" style={{ background: person.role.color }} aria-hidden="true"/> : null}{person.title || person.role?.name || person.tier}</small>
              </span>
            </li>)}</ul>
          </section>; })}</div>}
      </div>
    </section>
  </main>;
}

/* ---------- Ban appeal ---------- */

type Appeal = { status: "pending" | "approved" | "denied"; createdAt: string; decidedAt: string | null; decisionNote: string | null };
type BanState = { state: "member" | "banned" | "not_banned"; reason?: string | null; canAppeal?: boolean; appeal?: (Appeal & { statement: string }) | null; user?: { displayName: string; avatarUrl: string | null } };

export function AppealPage() {
  return <AreaGate id="public.appeal"><SiteShell><AppealBody/></SiteShell></AreaGate>;
}

function AppealCard({ title, description, children }: { title: string; description?: ReactNode; children?: ReactNode }) {
  return <section className="appeal-card" aria-labelledby="appeal-card-title">
    <h2 id="appeal-card-title">{title}</h2>
    {description && <p className="appeal-lead">{description}</p>}
    {children}
  </section>;
}

function AppealBody() {
  const { me } = useSite();
  const state = useResource<BanState>(me.user ? "/api/v1/appeals/ban" : null);
  const [statement, setStatement] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const length = statement.trim().length;
  const submit = () => {
    setBusy(true); setError(""); setSent(false);
    api("/api/v1/appeals/ban", { method: "POST", body: JSON.stringify({ statement }) })
      .then(() => { setStatement(""); setSent(true); state.reload(); })
      .catch((reason: ApiError) => setError(reason.error?.issues?.[0]?.message || reason.error?.message || "Your appeal could not be sent. Please try again."))
      .finally(() => setBusy(false));
  };
  const data = state.data;
  return <main id="main">
    <div className="container page-top"><header className="page-head"><h1>Appeal a ban</h1><p>If you were banned from the SkyBoxy Discord, you can ask the team to take another look. Be honest about what happened and what you would do differently.</p></header></div>
    <section className="band" aria-label="Your appeal">
      <div className="container appeal-grid">
        <div className="appeal-main">
          {!me.user ? <AppealCard title="Sign in to start your appeal" description="Use the Discord account that was banned. We only use it to confirm the ban and to send you the decision.">
            <div><Link className="button discord" to={loginPath("/appeal")}><DiscordMark size={16}/>Sign in with Discord</Link></div>
          </AppealCard>
            : state.loading && !data ? <Skeleton rows={3} label="Checking your ban"/>
            : state.error || !data ? <ErrorState error={state.error}/>
            : data.state === "member" ? <AppealCard title="You are not banned" description="You are still in the Discord. To appeal a warning or a timeout, use the History tab in your member portal.">
              <div><Link className="button primary" to="/discord/portal/history"><Icon name="history" size={16}/>Open my history</Link></div>
            </AppealCard>
            : data.state === "not_banned" ? <AppealCard title="No ban found" description={<>This Discord account is not banned from the server. If you left, you can rejoin from the <Link to="/join">join page</Link>.</>}/>
            : <AppealCard title="Your ban">
              <div className="appeal-person">
                <Avatar src={data.user?.avatarUrl} name={data.user?.displayName || "?"} size={44}/>
                <span><strong>{data.user?.displayName || "Your account"}</strong><Badge tone="danger">Banned</Badge></span>
              </div>
              <div className="appeal-reason"><span className="field-label">Reason given</span><p className="quote">{data.reason || "No reason was recorded."}</p></div>
              {sent && <Notice tone="good">Your appeal was sent. Staff will review it and you will hear back by Discord DM.</Notice>}
              {data.appeal && <AppealStatus appeal={data.appeal}/>}
              {data.canAppeal ? <form className="stack" onSubmit={(event) => { event.preventDefault(); submit(); }}>
                <Field label="Why should the ban be lifted?" hint={`${statement.length} of 2000 characters, at least 20. Staff see your Discord name, the ban reason, and this message.`}>
                  <textarea rows={7} minLength={20} maxLength={2000} required value={statement} onChange={(event) => setStatement(event.target.value)} placeholder="What happened, and what you will do differently"/>
                </Field>
                {error && <Notice tone="bad">{error}</Notice>}
                <div className="appeal-submit"><button type="submit" className="button primary" disabled={busy || length < 20}><Icon name="send" size={16}/>{busy ? "Sending…" : "Send appeal"}</button></div>
              </form> : data.appeal?.status === "denied" ? <p className="appeal-fine">You can send a new appeal 30 days after the last one was declined{data.appeal.decidedAt ? `, from ${formatDate(new Date(Date.parse(data.appeal.decidedAt) + 30 * 86400000).toISOString())}` : ""}.</p>
                : !data.appeal ? <p className="appeal-fine">Appeals are not open for this ban right now.</p> : null}
            </AppealCard>}
        </div>
        <aside className="appeal-how" aria-labelledby="appeal-how-title">
          <h2 id="appeal-how-title">How appeals work</h2>
          <ol>
            <li><strong>Sign in</strong><span>Use the Discord account that was banned.</span></li>
            <li><strong>Tell us what happened</strong><span>Explain it in your own words and say what you will do differently.</span></li>
            <li><strong>Wait for a decision</strong><span>Staff review it and decide. You get the answer by Discord DM when your settings allow it, and it always shows on this page.</span></li>
          </ol>
        </aside>
      </div>
    </section>
  </main>;
}

const appealCopy = {
  pending: { tone: "info", icon: "clock", title: "Waiting for staff" },
  approved: { tone: "success", icon: "check", title: "Appeal approved" },
  denied: { tone: "danger", icon: "alert", title: "Appeal declined" },
} as const;

export function AppealStatus({ appeal }: { appeal: Appeal }) {
  const copy = appealCopy[appeal.status] || appealCopy.pending;
  return <div className={`callout appeal-status tone-${copy.tone}`} role="status">
    <Icon name={copy.icon} size={20}/>
    <div>
      <strong>{copy.title}</strong>
      <span>{appeal.status === "pending" ? `Sent ${relative(appeal.createdAt)}. Staff will review it soon.` : `Decided ${formatDate(appeal.decidedAt)}.`}</span>
      {appeal.decisionNote && <p className="appeal-note">{appeal.decisionNote}</p>}
    </div>
  </div>;
}
